import { useAppTranslation } from 'app/config/i18Config/hooks';
import dayjs, { Dayjs } from 'dayjs';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { HistoryFilterKeys } from 'pages/HistoryPage/model/types';
import { FC, memo, useCallback } from 'react';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { CLIENT_DATE_FORMAT, CLIENT_DATE_WITH_MINUTE_TIME_FORMAT } from 'shared/utils/constants';
import { ActionTypeSelect } from './ActionTypeSelect';
import { WarehouseSelect } from './WarehouseSelect';

export const HistoryFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common', 'user']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<HistoryFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: HistoryFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changeStartDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: HistoryFilterKeys.startDate,
        label: t('From {{dueDateFrom}}', { dueDateFrom: clientFormatDateFrom }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  const changeEndDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: HistoryFilterKeys.endDate,
        label: t('To {{dueDateTo}}', { dueDateTo: clientFormatDateTo }),
        value,
        title: t('Date range'),
      });
    },
    [changeFilters, t],
  );

  const changeActionType = useCallback(
    (value: string[]) => {
      changeFilters({
        key: HistoryFilterKeys.actionTypes,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Action type'),
      });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Date range')}
          onChangeFrom={changeStartDate}
          onChangeTo={changeEndDate}
          startOfDay
          showTime
          format={CLIENT_DATE_WITH_MINUTE_TIME_FORMAT}
          values={[filters.startDate?.value && dayjs(filters.startDate?.value), filters.endDate?.value && dayjs(filters.endDate?.value)]}
        />

        <ActionTypeSelect value={filters.actionTypes?.value} onChange={changeActionType} onClear={clearCurrentFilters} />
      </div>
    </TableFilter>
  );
});
